/**
 * authCacheManager 兼容层 — 替代 @osp/osp-web3-login 的 authCacheManager
 *
 * 仅保留被应用代码实际使用的方法，读写同一 localStorage key 确保数据兼容
 */
import { LoginType } from "./sdk-types";
import type { IOspLoginInfo } from "./sdk-types";

const OSP_LOGIN_CACHE_KEY = "OSP_LOGIN_CACHE_V1";
const OWNER_INFO_CACHE_KEY = "OWNER_INFO_CACHE";

export interface OspLoginCache {
  accessToken?: string;
  refreshToken?: string;
  idToken?: string;
  loginType?: LoginType;
  loginInfo?: IOspLoginInfo;
  expiresAt: number;
  isRefreshAccessToken?: boolean;
}

export interface OwnerInfoCache {
  ownerInfo: any;
  timestamp: number;
  expiresAt: number;
  userId?: string;
}

function getStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export const authCacheManager = {
  getOspLoginCache(): OspLoginCache | null {
    const storage = getStorage();
    if (!storage) {
      return null;
    }
    try {
      const raw = storage.getItem(OSP_LOGIN_CACHE_KEY);
      if (!raw) {
        return null;
      }
      const cache: OspLoginCache = JSON.parse(raw);
      if (Date.now() >= cache.expiresAt) {
        storage.removeItem(OSP_LOGIN_CACHE_KEY);
        return null;
      }
      return cache;
    } catch {
      return null;
    }
  },

  getOwnerInfoCache(userId?: string): OwnerInfoCache | null {
    const storage = getStorage();
    if (!storage) {
      return null;
    }
    try {
      const raw = storage.getItem(OWNER_INFO_CACHE_KEY);
      if (!raw) {
        return null;
      }
      const cache: OwnerInfoCache = JSON.parse(raw);
      if (Date.now() >= cache.expiresAt) {
        storage.removeItem(OWNER_INFO_CACHE_KEY);
        return null;
      }
      if (userId && cache.userId && cache.userId !== userId) {
        return null;
      }
      return cache;
    } catch {
      return null;
    }
  },

  saveOwnerInfoCache(ownerInfo: any, userId?: string): void {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    try {
      const cache: OwnerInfoCache = {
        ownerInfo,
        timestamp: Date.now(),
        expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000,
        userId,
      };
      storage.setItem(OWNER_INFO_CACHE_KEY, JSON.stringify(cache));
    } catch {
      /* ignore */
    }
  },

  clearOspLoginCache(): void {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    try {
      storage.removeItem(OSP_LOGIN_CACHE_KEY);
    } catch {
      /* ignore */
    }
  },

  clearOwnerInfoCache(): void {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    try {
      storage.removeItem(OWNER_INFO_CACHE_KEY);
    } catch {
      /* ignore */
    }
  },

  clearAllCache(): void {
    this.clearOspLoginCache();
    this.clearOwnerInfoCache();
  },

  hasOspLoginCache(): boolean {
    return this.getOspLoginCache() !== null;
  },
};
